// Course catalog (/courses): grid or table of every course in the aura-courses
// Firestore project, with a status filter, search, per-card status change and
// delete. Reads work without the courses sign-in; writes need it.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconLayoutGrid, IconList, IconPencil, IconPlus, IconRefresh, IconTrash } from "@tabler/icons-react";
import { COURSE_STATUSES, formatMinutes, plural, type Course, type CourseStatus } from "../types/course";
import { deleteCourse, getAllCourses, updateCourseStatus } from "../services/courses/course.service";
import { errorText } from "../services/courses/auth";
import { useCoursesAuth } from "../hooks/useCoursesAuth";
import { StatusBadge } from "../components/StatusBadge";
import { CourseCover } from "../components/courses/CourseCover";
import { CourseStatusSelect } from "../components/courses/CourseStatusSelect";
import { CoursesCallouts } from "../components/courses/CoursesCallouts";
import { formatDate } from "../components/analytics/format";

type Filter = CourseStatus | "all";
type View = "grid" | "list";

const VIEW_KEY = "studio.courses.view";
// Tags shown on a card / table row before "+N" (the RAG function writes up to a dozen).
const CARD_TAGS = 4;
const ROW_TAGS = 3;

function capitalize(s: string) {
  return s[0].toUpperCase() + s.slice(1);
}

export function CourseListPage() {
  const navigate = useNavigate();
  const auth = useCoursesAuth();
  const canEdit = !!auth.user;

  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");
  const [view, setView] = useState<View>(() => (localStorage.getItem(VIEW_KEY) === "list" ? "list" : "grid"));

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setCourses(await getAllCourses());
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function changeView(v: View) {
    setView(v);
    localStorage.setItem(VIEW_KEY, v);
  }

  const counts = useMemo(() => {
    const c: Record<Filter, number> = { all: courses.length, draft: 0, approved: 0, published: 0, archived: 0 };
    courses.forEach((course) => {
      c[course.status] = (c[course.status] ?? 0) + 1;
    });
    return c;
  }, [courses]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return courses.filter(
      (c) =>
        (filter === "all" || c.status === filter) &&
        (!q ||
          c.title.toLowerCase().includes(q) ||
          c.description.toLowerCase().includes(q) ||
          (c.tags ?? []).some((t) => t.toLowerCase().includes(q))),
    );
  }, [courses, filter, search]);

  const open = (id: string) => navigate(`/courses/${id}`);

  async function changeStatus(course: Course, status: CourseStatus) {
    const previous = course.status;
    setCourses((cur) => cur.map((c) => (c.id === course.id ? { ...c, status } : c)));
    setNotice(null);
    try {
      await updateCourseStatus(course.id!, status);
    } catch (e) {
      setCourses((cur) => cur.map((c) => (c.id === course.id ? { ...c, status: previous } : c)));
      setNotice(`Status change failed: ${errorText(e)}`);
    }
  }

  async function remove(course: Course) {
    const lessons = plural(course.totalLessons ?? 0, "lesson");
    if (!confirm(`Delete “${course.title || "Untitled course"}”? Its ${lessons} and their videos go with it.`)) return;
    setNotice(null);
    try {
      await deleteCourse(course.id!);
      setCourses((cur) => cur.filter((c) => c.id !== course.id));
    } catch (e) {
      setNotice(`Delete failed: ${errorText(e)}`);
    }
  }

  function renderActions(course: Course, withEdit: boolean) {
    return (
      <>
        {withEdit && (
          <button type="button" className="btn btn-sm" onClick={(e) => { e.stopPropagation(); open(course.id!); }}>
            <IconPencil size={13} /> Edit
          </button>
        )}
        <CourseStatusSelect value={course.status} disabled={!canEdit} label={`Status of ${course.title}`}
          onChange={(s) => changeStatus(course, s)} />
        <div className="spacer" />
        <button type="button" className="co-btn-icon danger" disabled={!canEdit} title="Delete course" aria-label="Delete course"
          onClick={(e) => { e.stopPropagation(); remove(course); }}>
          <IconTrash size={14} />
        </button>
      </>
    );
  }

  return (
    <div className="list-page">
      <div className="list-toolbar">
        <h1>Courses</h1>
        <input
          className="search-box"
          type="search"
          aria-label="Search courses"
          placeholder="Search title, description, tags"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="spacer" />
        <button type="button" className="btn" onClick={load} disabled={loading}>
          <IconRefresh size={14} /> Refresh
        </button>
        <button type="button" className="btn btn-primary" onClick={() => navigate("/courses/new")} disabled={!canEdit}>
          <IconPlus size={14} /> New course
        </button>
      </div>

      <CoursesCallouts auth={auth} />
      {notice && <div className="callout co-callout co-callout-error">{notice}</div>}

      <div className="filters co-filters">
        <div className="seg" role="tablist" aria-label="Status filter">
          {(["all", ...COURSE_STATUSES] as Filter[]).map((f) => (
            <button key={f} type="button" role="tab" aria-selected={filter === f} className={filter === f ? "active" : ""}
              onClick={() => setFilter(f)}>
              {capitalize(f)} <span className="co-cnt">{counts[f]}</span>
            </button>
          ))}
        </div>
        <div className="spacer" />
        <div className="seg" aria-label="View">
          <button type="button" className={view === "grid" ? "active" : ""} title="Grid" aria-label="Grid view"
            onClick={() => changeView("grid")}>
            <IconLayoutGrid size={15} />
          </button>
          <button type="button" className={view === "list" ? "active" : ""} title="List" aria-label="List view"
            onClick={() => changeView("list")}>
            <IconList size={15} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="state-box">Loading courses…</div>
      ) : error ? (
        <div className="state-box error">{error}</div>
      ) : (
        <>
          <div className="result-count">
            {filtered.length} of {plural(courses.length, "course")} · <span className="mono">aura-courses</span> Firestore ·{" "}
            {auth.user ? `signed in to the courses project as ${auth.user.email}` : "not signed in to the courses project"}
          </div>

          {filtered.length === 0 ? (
            <div className="state-box">
              {courses.length === 0 ? "No courses yet." : "No courses match this filter."}
              {courses.length === 0 && canEdit && (
                <div style={{ marginTop: 16 }}>
                  <button type="button" className="btn btn-primary" onClick={() => navigate("/courses/new")}>
                    <IconPlus size={14} /> New course
                  </button>
                </div>
              )}
            </div>
          ) : view === "grid" ? (
            <div className="co-grid">
              {filtered.map((c) => (
                <div key={c.id} className="co-card" role="link" tabIndex={0} onClick={() => open(c.id!)}
                  onKeyDown={(e) => { if (e.key === "Enter") open(c.id!); }}>
                  <CourseCover course={c} />
                  <div className="co-card-body">
                    <div className="co-card-head">
                      <h3>{c.title || <span className="muted">Untitled course</span>}</h3>
                      <StatusBadge status={c.status} />
                    </div>
                    <p className="co-card-desc">{c.description || <span className="muted">No description yet.</span>}</p>
                    <div className="co-card-meta">
                      <span>{plural(c.totalLessons ?? 0, "lesson")}</span>
                      <span>·</span>
                      <span>{formatMinutes(c.totalDuration)}</span>
                    </div>
                    <div className="co-card-tags">
                      {(c.tags ?? []).length ? (
                        <>
                          {c.tags!.slice(0, CARD_TAGS).map((t) => <span key={t} className="chip">{t}</span>)}
                          {c.tags!.length > CARD_TAGS && <span className="chip">+{c.tags!.length - CARD_TAGS}</span>}
                        </>
                      ) : (
                        <span className="muted tt-small">No tags yet</span>
                      )}
                    </div>
                    <div className="co-card-foot">{renderActions(c, true)}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="table-scroll">
              <table className="guide-table co-table">
                <thead>
                  <tr>
                    <th className="no-sort">Course</th>
                    <th className="no-sort">Status</th>
                    <th className="no-sort">Lessons</th>
                    <th className="no-sort">Duration</th>
                    <th className="no-sort">Tags</th>
                    <th className="no-sort">Updated</th>
                    <th className="no-sort" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => (
                    <tr key={c.id} className="guide-row" onClick={() => open(c.id!)}>
                      <td>
                        <div className="guide-name">
                          <CourseCover course={c} small />
                          <div style={{ minWidth: 0 }}>
                            <div className="name">{c.title || <span className="muted">Untitled course</span>}</div>
                            <div className="role">{c.description || "—"}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <CourseStatusSelect value={c.status} disabled={!canEdit} label={`Status of ${c.title}`}
                          onChange={(s) => changeStatus(c, s)} />
                      </td>
                      <td className="voice-cell">{c.totalLessons ?? 0}</td>
                      <td className="voice-cell">{formatMinutes(c.totalDuration)}</td>
                      <td>
                        <div className="co-card-tags">
                          {(c.tags ?? []).slice(0, ROW_TAGS).map((t) => <span key={t} className="chip">{t}</span>)}
                          {(c.tags ?? []).length > ROW_TAGS && <span className="chip">+{c.tags!.length - ROW_TAGS}</span>}
                        </div>
                      </td>
                      <td className="voice-cell">{c.updatedAt ? formatDate(c.updatedAt.toISOString()) : "—"}</td>
                      <td>
                        <div className="row-actions">
                          <button type="button" className="btn btn-sm" onClick={(e) => { e.stopPropagation(); open(c.id!); }}>
                            Edit
                          </button>
                          <button type="button" className="co-btn-icon danger" disabled={!canEdit} title="Delete course"
                            aria-label="Delete course" onClick={(e) => { e.stopPropagation(); remove(c); }}>
                            <IconTrash size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
